/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect } from 'react'
import { useServiceContext } from '../../root'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { RcmTable } from '../../components/rcm-table/RcmTable'
import {
  TableMultiSelectionWrapper,
  SelectAllRows,
  SelectRow,
  TableMultiSelectionConsumer
} from '../../components/rcm-table/TableMultiSelection'
import { TableHeadRow, TableHeadCell, TableRow } from '../../components/table/TableComponents'
import TextLinkButton from '../../components/shared/TextLinkButton'
import TableDateTimeCell from '../../components/shared/TableDateTimeCell'
import { AclLevel, SystemFolderType } from '../../constants/fsi'
import DocState from '../../components/shared/DocState'
import { DocumentsViewType } from './types'
import useDocumentsListService from '../../hooks/useDocumentsListService'
import ReportsTableMenu from './ReportsTableMenu'

interface ReportsTableProps {
  viewType: DocumentsViewType
  fsi: any
}

export const ReportsTable: React.FC<ReportsTableProps> = ({ viewType, fsi }) => {
  const context = useServiceContext()
  const tableData = useRcmTableData()
  const { assembleReportFiltersFromFormValues } = useDocumentsListService()
  const {
    service: {
      filter: { translate }
    },
    api
  } = context

  useEffect(() => {
    // Listen for table update events via window event bus
    const handleUpdateTable = () => {
      tableData.refreshTable()
    }

    window.addEventListener('bctable:updateTable', handleUpdateTable)
    window.addEventListener('updateFiles', handleUpdateTable)

    return () => {
      window.removeEventListener('bctable:updateTable', handleUpdateTable)
      window.removeEventListener('updateFiles', handleUpdateTable)
    }
  }, [tableData, context.bus])

  const canShowMultiSelect = () => {
    return true
  }

  return (
    <div>
      <TableMultiSelectionWrapper
        texts={{
          selectAllItemsText: 'DOCUMENTS_MULTISELECT_SELECT_ALL_IN_SECTION',
          allItemsSelectedText: 'DOCUMENTS_MULTISELECT_ALL_N_ITEMS_SELECTED'
        }}
        textParams={{
          folderPathText: fsi?.folderPathText || ''
        }}
        getTotalNumberFunction={(filters: any) => {
          return api.reports.getReportsCountReact(filters)
        }}
        assembleFilterFunction={(filters: any, flag: boolean) => {
          return assembleReportFiltersFromFormValues({ ...filters, viewType })
        }}
      >
        <RcmTable
          className="chita-table fsiTable"
          noResultsText={'NO_RESULTS_FILTERED'}
          simplePaginate={true}
          HeadRow={
            <TableHeadRow className={'has-actions'}>
              <th className="clipped-cell with-checkbox" id="c6ba22ec-1b80-4166-b541-9e3dba910ab7">
                <SelectAllRows />
              </th>
              <TableHeadCell id="fb447770-d1c6-47cb-bac9-abc46fb2349f" sort={'name'}>
                {translate('REPORTS_TABLE_TH_DOCUMENT_NAME')}
              </TableHeadCell>
              {(viewType as any) !== SystemFolderType.EMAIL_UPLOADS && (
                <TableHeadCell className="narrow" id="1604015b-c256-4d0a-b7af-ce9370ca8edd">
                  {translate('TABLE_TH_REPORT_TYPE')}
                </TableHeadCell>
              )}
              <TableHeadCell className="narrow" id="0ebdb0be-f75f-4834-9f83-53e82ca62b95" sort={'createdAt'}>
                {translate('CREATED_ON')}
              </TableHeadCell>
              <TableHeadCell className="narrow" id="c1288a38-a5f0-4334-9f59-d0b5bd8eecb9">
                {translate('CREATED_BY')}
              </TableHeadCell>
              <TableHeadCell id="4bbaf99c-1c90-46b2-a6ca-089351ce13ca" className="actions">
                {translate('ACTIONS')}
              </TableHeadCell>
            </TableHeadRow>
          }
          Row={(props) => {
            const row = props.row
            return (
              <TableRow {...props}>
                {canShowMultiSelect() && (
                  <td className="clipped-cell with-checkbox">
                    <SelectRow row={row} disabled={row.workflow || !row.valid} />
                  </td>
                )}
                <td className="name clipped-cell">
                  <div className="d-flex align-left">
                    <div className="ellipsis" title={row.name}>
                      {[AclLevel.BROWSE, AclLevel.NONE].includes(row.acl) || !row.valid ? (
                        <span>{row.name}</span>
                      ) : (
                        <TextLinkButton
                          action={async () => {
                            try {
                              const data = await api.files.getFileDownloadUrl(row.id)
                              if (data && data.url) {
                                window.open(data.url, '_blank')
                              }
                            } catch (error) {
                              console.error('Failed to download report:', error)
                            }
                          }}
                        >
                          {row.name}
                        </TextLinkButton>
                      )}
                    </div>
                    <DocState fsi={row} />
                  </div>
                </td>
                {(viewType as any) !== SystemFolderType.EMAIL_UPLOADS && (
                  <td className="narrow whitespacenowrap">
                    {row.systemFolderTypeLocalized || translate('NOT_AVAILABLE')}
                  </td>
                )}
                <td className="narrow updated">
                  <TableDateTimeCell val={row.createdAt} includeTime={true} />
                </td>
                <td className="narrow whitespacenowrap">
                  {row.createdById && row.createdBy
                    ? row.createdBy?.firstName + ' ' + row.createdBy?.lastName
                    : translate('NOT_AVAILABLE')}
                </td>
                <td className="actions">
                  <TableMultiSelectionConsumer>
                    {(tableMultiSelection) => {
                      return ![AclLevel.BROWSE, AclLevel.NONE].includes(row.acl) && row.valid ? (
                        <ReportsTableMenu
                          fsi={row}
                          options={{
                            ...(tableMultiSelection?.multiSelectState?.selectedIds
                              ? {
                                  selectedItems: tableData.entries.filter((i: any) =>
                                    tableMultiSelection?.multiSelectState?.selectedIds.includes(i.id)
                                  )
                                }
                              : null),
                            ...(tableMultiSelection?.multiSelectState || {}),
                            deselectAll: tableMultiSelection?.deselectAll
                          }}
                        />
                      ) : null
                    }}
                  </TableMultiSelectionConsumer>
                </td>
              </TableRow>
            )
          }}
        />
      </TableMultiSelectionWrapper>
    </div>
  )
}
